import { Box, Divider, Flex, Grid, Text, Title} from "@mantine/core";
import {jwtDecode} from "jwt-decode";
import {ManageOrganization} from "../../ui/Organization/customer/ManageOrganization";
import {sumAllPastRevenue} from "../../ui/information/Customer/totalRevenue";
import {RevenueLast6MonthsChart} from "../../ui/information/Customer/RevenueLast6MonthChart";
import {DonutChart} from "@mantine/charts";
import {ContractsBarChart} from "../../ui/information/Customer/ContractBarChart";
import React, {useEffect, useState} from "react";
import {Organization} from "../../models/Organization";
import {RentContract} from "../../models/RentContract";
import {getToken, getUserIdFromToken} from "../../auth/service/authService";
import {fetchMyOrganization} from "../../Services/OrganizationService";
import {
    filterActiveRentContracts,
    getRentContractsByOrganization
} from "../../Services/RentContractService";

import {NoMembership} from "../../ui/Organization/customer/NoMembership";
import {MembershipRole} from "../../models/Membership";

export const Overview = () => {

    const [organization, setOrganization] = useState<Organization | null>(null);
    const [contracts, setContracts] = useState<RentContract[] | null>(null);

    const userId = getUserIdFromToken();
    const token = getToken()

    useEffect(() => {

            fetchMyOrganization().then((response) => {
                if(response){
                    setOrganization(response);
                }

            });


    }, []);

    useEffect(() => {
        if (organization) {
            getRentContractsByOrganization(organization.id).then((response) => {
                setContracts(response);
            });
        }
    }, [organization]);

    // Refresh data after certain actions (e.g. after creating a location or rent contract)
    function updatePage() {
        fetchMyOrganization().then((response) => {
            setOrganization(response);
        });
    }

    // Calculate stats for charts
    const activeCount = filterActiveRentContracts(contracts)?.length ?? 0;
    const totalLocs = organization?.storageLocations?.length ?? 0;
    const chartData = [
        { name: "Vermietet", value: activeCount, color: "green" },
        { name: "Unvermietet", value: totalLocs - activeCount, color: "red" },
    ];

    // If user has no organization, show a fallback
    if (!organization) {
        return (
            <NoMembership />
        );
    }
    return <Grid gutter="md">
        {/* Organization header row */}
        <Grid.Col span={12}>
            <Flex align="center" justify="space-between" wrap="wrap">
                {token && <Text>Hallo, {jwtDecode(token).sub}</Text>}
            </Flex>
        </Grid.Col>

        <Grid.Col span={12}>
            <Divider />
        </Grid.Col>

        {/* Organization header row */}
        <Grid.Col span={12}>
            <Flex align="center" justify="space-between" wrap="wrap">
                <Flex direction={"row"} gap={"15px"}>
                    <Title order={3}>{organization.name}</Title>
                    <Text>*Die berechneten Werte basieren auf den Daten aus dem Lagerfirst Tool und nicht auf real umgesetzten Summen</Text>
                </Flex>

                {userId && isUserAdmin(organization, userId) && (
                    <ManageOrganization organization={organization} onOrgUpdated={updatePage} />
                )}
            </Flex>
        </Grid.Col>



        {/* Top stats row: 3 columns (Revenue, Storage, Contracts).
          On smaller screens each becomes full-width (span=12). */}
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Title order={4}>Gesamter Umsatz</Title>
            {contracts && <Title order={2}>{sumAllPastRevenue(contracts)} €</Title>}
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Title order={4}>Alle Stellplätze</Title>
            <Title order={2}>{organization.storageLocations.length}</Title>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Title order={4}>Alle Mietverträge</Title>
            <Title order={2}>{contracts?.length}</Title>
        </Grid.Col>

        <Grid.Col span={12}>
            <Divider />
        </Grid.Col>

        {/* Charts row: 3 columns for charts. Each chart goes full width below md. */}
        <Grid.Col span={{ base: 12, md: 4 }}>
            <Title order={4} mb="xs">
                Umsatz der letzten 6 Monate
            </Title>
            {contracts && (
                <RevenueLast6MonthsChart contracts={contracts} />
            )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
            <Title order={4} mb="xs">
                Aktuelle Auslastung
            </Title>
            {contracts && (
                <Flex w={"100%"} justify={"center"}>
                    <DonutChart withLabelsLine labelsType="value" withLabels data={chartData} />
                </Flex>
            )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
            <Title order={4} mb="xs">
                Beginn / Ende von Verträgen
            </Title>
            {contracts && (
                <Box style={{ minWidth: 250, minHeight: 200 }}>
                    <ContractsBarChart contracts={contracts} />
                </Box>
            )}
        </Grid.Col>

        <Grid.Col span={12}>
            <Divider />
        </Grid.Col>
    </Grid>
}

/**
 * Checks if the given user (by userId) is an ADMIN in the specified organization.
 *
 * @param org - The organization object
 * @param userId - The user ID string to check
 * @returns true if user has an ADMIN membership in the org, otherwise false
 */
export function isUserAdmin(org: Organization, userId: string): boolean {
    return org.memberships.some((membership) => {
        return (
            membership.userKey?.userId === userId &&
            membership.membershipRole === MembershipRole.ADMIN
        );
    });
}